import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import socketClient from '../services/socketClient';
import useAuth from '../hooks/useAuth';
import { useAnalytics, useTimeTracking } from '../hooks/useAnalytics';
import useGameWebSocket from '../hooks/useGameWebSocket';
import useWriteInStats from '../hooks/useWriteInStats';
import useWagering from '../hooks/useWagering';
import useGameTimer from '../hooks/useGameTimer';
import useWriteInCards from '../hooks/useWriteInCards';
import useBlackCardWriteIn from '../hooks/useBlackCardWriteIn';
import useGameEventHandlers from './gameRoom/useGameEventHandlers';
import Card, { BlackCard } from './common/Card';
import WriteInModal from './modals/WriteInModal';
import BlackCardModal from './modals/BlackCardModal';
import PresentationPhase from './phases/PresentationPhase';
import VotingPhase from './phases/VotingPhase';
import ResultsPhase from './phases/ResultsPhase';
import GameFinished from './phases/GameFinished';
import Scoreboard from './Scoreboard';
import { ToastContainer } from './common/Toast';
import './GameRoom.css';

const GameRoom = () => {
  const { roomCode: urlRoomCode } = useParams();
  const navigate = useNavigate();
  const { requireAuth, username, getRoomData, clearRoomData } = useAuth();

  // Analytics tracking
  const {
    trackRoundStarted,
    trackRoundCompleted,
    trackCardSubmitted,
    trackVoteCast,
    trackGameCompleted,
    trackPlayerDropoff,
    trackConnectionStatus,
    trackError,
    trackFeature,
  } = useAnalytics();

  // Track time spent in game room
  useTimeTracking('game_room');

  // Get room code from URL params OR from hook
  const { roomCode: storedRoomCode } = getRoomData();
  const roomCode = urlRoomCode || storedRoomCode;

  const [gameState, setGameState] = useState('loading');
  const [players, setPlayers] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [judge, setJudge] = useState(null);
  const [nextJudge, setNextJudge] = useState(null); // NEW: Store the next Judge for results display
  const [currentBlackCard, setCurrentBlackCard] = useState(null);
  const [previousBlackCard, setPreviousBlackCard] = useState(null); // NEW: Store the old black card
  const [playerHand, setPlayerHand] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [presentationState, setPresentationState] = useState({
    isActive: false,
    currentIndex: -1,
    totalSubmissions: 0,
    currentSubmission: null,
    showAllSubmissions: false,
  });
  const [selectedCards, setSelectedCards] = useState([]);
  const [roundWinner, setRoundWinner] = useState(null);
  // ISSUE #271: RHP Judge Double Award picks 2 winning submissions; backend sends
  // both via `winners` + `isDoubleAward` on round-ended, separate from the legacy
  // single `winner` field kept for backward compatibility.
  const [roundWinners, setRoundWinners] = useState([]);
  const [isDoubleAwardRound, setIsDoubleAwardRound] = useState(false);
  const [winningSubmission, setWinningSubmission] = useState(null); // NEW: Store the winning submission
  const [pointsAwarded, setPointsAwarded] = useState(null); // #190: Points awarded to winner
  const [pointsBreakdown, setPointsBreakdown] = useState(null); // #190: Points breakdown (base + pot)
  const [scores, setScores] = useState({});
  const [round, setRound] = useState(1);
  const [maxRounds, setMaxRounds] = useState(3); // FIXED: Default to 3, will be updated from backend
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [error, setError] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState({ submitted: 0, total: 0 });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const [totalRoundsPlayed, setTotalRoundsPlayed] = useState(0);
  const [resultsCountdown, setResultsCountdown] = useState(null);
  const [isRoomCreator, setIsRoomCreator] = useState(false);
  const [roomCreatorPlayerId, setRoomCreatorPlayerId] = useState(null);
  const [isDeputy, setIsDeputy] = useState(false);
  const [deputyPlayerId, setDeputyPlayerId] = useState(null);
  const [coHostEnabled, setCoHostEnabled] = useState(false); // CO-HOST SYSTEM: Track if co-host feature is enabled
  const [designatedFirstCzarId, setDesignatedFirstCzarId] = useState(null); // POOP RULE

  // SPECTATOR PROMOTION: State for room capacity and spectator promotion
  const [roomHasSpace, setRoomHasSpace] = useState(true); // Track if room has space for spectators to join
  const [allowSpectatorPromotion, setAllowSpectatorPromotion] = useState(true); // Track if spectator promotion is enabled

  // NEW: First-time tooltip state
  const [showFirstTimeTooltip, setShowFirstTimeTooltip] = useState(false);
  const tooltipTimeoutRef = useRef(null);

  // TIMER SYSTEM: State for countdown timers
  const {
    timerActive,
    timerType,
    timerDuration,
    timerRemaining,
    timerMessage,
    setTimerActive,
    setTimerType,
    setTimerDuration,
    setTimerRemaining,
    setTimerMessage,
    setTimerTargetPlayer,
    timerIntervalRef,
  } = useGameTimer();

  // WRITE-IN CARDS SYSTEM: State for custom card creation
  const [writeInsEnabled, setWriteInsEnabled] = useState(false);

  // RHP DISCARD MECHANISM: State for card discarding feature
  const [rhpDiscardEnabled, setRhpDiscardEnabled] = useState(false);
  const [playerWhitePrivilegeBonus, setPlayerWhitePrivilegeBonus] = useState(0);
  const [playerDiscardCount, setPlayerDiscardCount] = useState(0);
  const [cardsToDiscard, setCardsToDiscard] = useState([]);
  const [isDiscarding, setIsDiscarding] = useState(false);

  // RHP JUDGE DOUBLE AWARD: State for once-per-game double winner selection
  const [rhpDoubleAwardEnabled, setRhpDoubleAwardEnabled] = useState(false);
  const [doubleAwardUsedMap, setDoubleAwardUsedMap] = useState({});

  // RHP ENHANCED WAGERING: State for wagering more than 1 chip at a time
  const [rhpEnhancedWageringEnabled, setRhpEnhancedWageringEnabled] = useState(false);

  // TOAST NOTIFICATIONS: State for displaying rate limit and error messages
  const [toasts, setToasts] = useState([]);

  // MEMORY LEAK FIX: Use ref to track if component is mounted
  const mountedRef = useRef(true);

  // MEMORY LEAK FIX: Store cleanup functions
  const cleanupFunctionsRef = useRef([]);

  // ROUND SKIPPED: Debounce flag to prevent duplicate alerts
  const roundSkippedShownRef = useRef(false);

  // Guards against initializeGameFromBackend running twice concurrently -
  // both Finding 6's "player not found" fallback and Finding 14's retry-on-
  // reconnect can call it, and if the underlying 'connect' event ever fires
  // more than once in quick succession, two overlapping calls can each
  // independently reach the join-room fallback and fire a duplicate join
  // (confirmed live: this evicted a bot that didn't need to go).
  const isInitializingRef = useRef(false);

  // WRITE-IN RATE LIMITING: Track write-in usage and limits
  const {
    stats: writeInStats,
    limits: writeInLimits,
    refetch: refetchWriteInStats,
  } = useWriteInStats(roomCode, currentPlayer?.id, round);

  // TOAST NOTIFICATION HELPERS
  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // WAGERING SYSTEM: Custom hook for wagering state
  const {
    isWagering,
    setIsWagering,
    wageringPlayers,
    setWageringPlayers,
    canWager,
    chipsWagered,
    setChipsWagered,
    maxChips,
  } = useWagering(
    scores,
    gameState,
    hasSubmitted,
    currentPlayer,
    judge,
    username,
    round,
    playerHand.length,
    currentBlackCard?.pick || 1
  );

  // Simplified card rendering using the new Card component
  const renderCard = (
    card,
    type = 'white',
    isSelected = false,
    onClick = null,
    isWriteIn = false,
    selectionOrder = null,
    cardRef = null,
    wagerGroup = null
  ) => {
    return (
      <Card
        key={card.id || card.text}
        ref={cardRef}
        card={card}
        type={type}
        isSelected={isSelected}
        isClickable={!!onClick}
        onClick={onClick}
        isWriteIn={isWriteIn}
        selectionOrder={selectionOrder}
        wagerGroup={wagerGroup}
      />
    );
  };

  const renderPresentationPhase = () => {
    const judgeUsername = judge?.username || 'Unknown';
    const isJudge = username === judgeUsername;

    return (
      <PresentationPhase
        presentationState={presentationState}
        isJudge={isJudge}
        judgeUsername={judgeUsername}
        currentBlackCard={currentBlackCard}
        onStartPresentation={handleStartPresentation}
        onNextSubmission={handleNextSubmission}
      />
    );
  };

  const renderPlayerHand = () => {
    const judgeUsername = judge?.username || 'Unknown';
    const isJudge = username === judgeUsername;

    console.log('🔍 Judge check:', { username, judgeUsername, isJudge });

    if (isJudge) {
      return (
        <div className="judge-message">
          <div className="judge-message-content">
            <h3>You are the Judge this round!</h3>
            <p>Wait for players to submit their cards, then choose the funniest combination.</p>
            <p>Your answer cards are hidden while you judge - you'll see them again next round!</p>
            {submissionStatus.total > 0 && (
              <p>
                Submissions: {submissionStatus.submitted}/{submissionStatus.total}
              </p>
            )}
          </div>

          {/* Judge Controls - Show buttons during 'czar-picking' phase */}
          {gameState === 'czar-picking' && (
            <div className="czar-controls">
              <button className="czar-btn start-round-btn" onClick={confirmBlackCard}>
                🚀 Start Round
              </button>
              <button
                className="czar-btn write-black-card-btn"
                onClick={openBlackCardModal}
                disabled={czarWriteInTimer > 0}
              >
                ✍️ Write Custom Question Card
              </button>
              <button
                className="czar-btn skip-black-card-btn"
                onClick={skipBlackCard}
                disabled={hasSkippedThisRound}
              >
                ⏭️ {hasSkippedThisRound ? 'Skip Used' : 'Skip Card'}
              </button>
            </div>
          )}
        </div>
      );
    }

    // Show waiting message during czar-picking phase for non-czar players
    if (gameState === 'czar-picking') {
      return (
        <div className="czar-picking-message">
          <h3>⏳ {judgeUsername} is choosing the question card...</h3>
          <p>
            {judgeUsername} is reviewing the question card and deciding whether to keep it, skip it,
            or write a custom one.
          </p>
          <p>Please wait while they make their selection.</p>
        </div>
      );
    }

    // SPECTATOR CHECK: Spectators should not see card submission UI
    if (currentPlayer?.isSpectator) {
      return (
        <div className="spectator-view">
          <h3>👁️ Voyeur View</h3>
          <p>You are watching this game as a voyeur.</p>
          <p>You cannot submit cards or participate in gameplay.</p>
          {allowSpectatorPromotion && (
            <div className="spectator-promotion">
              <button
                className="join-as-player-btn"
                onClick={handlePromoteSpectator}
                disabled={!roomHasSpace}
                title={
                  roomHasSpace ? 'Join as a player' : 'Room is full - wait for a slot to open up'
                }
              >
                {roomHasSpace ? 'Join as Player' : 'Room Full'}
              </button>
              {!roomHasSpace && (
                <p className="room-capacity-info">Wait for a player to leave or be removed.</p>
              )}
            </div>
          )}
        </div>
      );
    }

    // CRITICAL: Debug card structure
    console.log('🔍 renderPlayerHand - playerHand:', playerHand);
    console.log('🔍 renderPlayerHand - playerHand.length:', playerHand?.length);
    console.log('🔍 First card structure:', playerHand?.[0]);

    if (!playerHand || !Array.isArray(playerHand) || playerHand.length === 0) {
      return (
        <div className="no-cards-message">
          <h3>Loading your cards...</h3>
          <p>Please wait while we deal your hand.</p>
          <p>Debug: playerHand = {JSON.stringify(playerHand || 'null')}</p>
        </div>
      );
    }

    const baseCardsNeeded = currentBlackCard?.pick || 1;
    const cardsNeeded = baseCardsNeeded * (1 + (isWagering ? chipsWagered : 0));
    const canSubmit = selectedCards.length === cardsNeeded && !hasSubmitted;

    return (
      <div className="player-hand">
        <div className="hand-header">
          <h3>Your Cards</h3>
          <div className="selection-info">
            {gameState === 'playing' && !hasSubmitted && (
              <>
                <div className="card-selection-instructions">
                  <span className="instruction-text">
                    Select {cardsNeeded} card{cardsNeeded > 1 ? 's' : ''}{' '}
                    {isWagering && `(+${chipsWagered} for wager${chipsWagered > 1 ? 's' : ''})`} -
                    click again to deselect
                  </span>
                </div>

                {/* CHIP COUNT */}
                {(() => {
                  const myChips = scores[currentPlayer?.id] || scores[currentPlayer?.socketId] || 0;
                  return (
                    <div className="chip-count" title="Your Buffalo Chips">
                      💰 {myChips} {myChips === 1 ? 'Chip' : 'Chips'}
                    </div>
                  );
                })()}

                {/* WAGERING CONTROLS */}
                <div className="wagering-controls">
                  {canWager && rhpEnhancedWageringEnabled && (
                    <div className="chip-wager-selector">
                      <label htmlFor="chips-wagered-input">Wager</label>
                      <button
                        type="button"
                        className="chip-stepper-btn"
                        onClick={() => adjustWager(chipsWagered - 1)}
                        disabled={chipsWagered <= 1}
                        aria-label="Decrease chips wagered"
                      >
                        −
                      </button>
                      <input
                        id="chips-wagered-input"
                        type="number"
                        min={1}
                        max={maxChips}
                        value={chipsWagered}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          if (!Number.isNaN(value)) {
                            adjustWager(value);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="chip-stepper-btn"
                        onClick={() => adjustWager(chipsWagered + 1)}
                        disabled={chipsWagered >= maxChips}
                        aria-label="Increase chips wagered"
                      >
                        +
                      </button>
                      <span>chip{chipsWagered > 1 ? 's' : ''}</span>
                    </div>
                  )}

                  {canWager && !isWagering && (
                    <button
                      className="wager-btn start-wager"
                      onClick={handleStartWager}
                      title={
                        rhpEnhancedWageringEnabled
                          ? `Bet ${chipsWagered} Buffalo Chip${chipsWagered > 1 ? 's' : ''} to play ${chipsWagered} extra card${chipsWagered > 1 ? 's' : ''}. Win: +${chipsWagered} Chips on top of the normal win. Lose: -${chipsWagered} Chip${chipsWagered > 1 ? 's' : ''}.`
                          : 'Bet 1 Buffalo Chip to play an extra card. Win: +2 Chips total. Lose: -1 Chip.'
                      }
                    >
                      💰{' '}
                      {rhpEnhancedWageringEnabled
                        ? `Bet ${chipsWagered} Chip${chipsWagered > 1 ? 's' : ''}`
                        : 'Bet a Chip'}
                    </button>
                  )}

                  {isWagering && (
                    <button
                      className="wager-btn cancel-wager"
                      onClick={handleCancelWager}
                      title="Cancel your wager"
                    >
                      ❌ Cancel Wager
                      {isWagering && chipsWagered > 1 ? ` (${chipsWagered} chips)` : ''}
                    </button>
                  )}

                  {!canWager &&
                    (scores[currentPlayer?.id] || scores[currentPlayer?.socketId] || 0) < 1 && (
                      <div className="wager-disabled">
                        <span title="You need at least 1 Buffalo Chip to wager">
                          💰 Need a Chip to wager
                        </span>
                      </div>
                    )}

                  {!canWager &&
                    (scores[currentPlayer?.id] || scores[currentPlayer?.socketId] || 0) >= 1 &&
                    playerHand.length < baseCardsNeeded * 2 && (
                      <div className="wager-disabled">
                        <span title="You don't have enough cards in hand to wager this round">
                          🃏 Not enough cards to wager
                        </span>
                      </div>
                    )}
                </div>

                {/* WRITE-IN CARDS: Write-in button and controls */}
                {writeInsEnabled && (
                  <div className="write-in-controls">
                    <button
                      className="write-in-btn"
                      onClick={openWriteInModal}
                      disabled={
                        hasSubmitted ||
                        writeInCards.length >= Math.min(2, currentBlackCard?.pick || 1)
                      }
                    >
                      ✍️ Write Custom Card ({writeInCards.length}/
                      {Math.min(2, currentBlackCard?.pick || 1)})
                    </button>

                    {writeInCards.length > 0 && (
                      <div className="write-in-list">
                        {writeInCards.map((card) => (
                          <div key={card.id} className="write-in-item">
                            <span>"{card?.text || 'Invalid write-in'}"</span>
                            <button
                              onClick={() => removeWriteInCard(card.id)}
                              className="remove-write-in-btn"
                              disabled={hasSubmitted}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* RHP DISCARD MECHANISM: Discard controls */}
                {rhpDiscardEnabled && !isDiscarding && (
                  <div className="discard-controls">
                    <button
                      className="discard-btn start-discard"
                      onClick={handleStartDiscard}
                      disabled={hasSubmitted || playerHand.length < 3}
                      title={
                        playerHand.length < 3
                          ? 'Not enough cards to discard'
                          : 'Trade 2 cards for 1 new card. Reduces max hand size by 1.'
                      }
                    >
                      🔄 Discard 2 for 1
                    </button>
                    <span className="max-hand-size-indicator">
                      {playerHand.length} cards
                      {(playerWhitePrivilegeBonus > 0 || playerDiscardCount > 0) &&
                        ` (${[
                          playerWhitePrivilegeBonus > 0 && `+${playerWhitePrivilegeBonus} WP`,
                          playerDiscardCount > 0 && `-${playerDiscardCount} discard`,
                        ]
                          .filter(Boolean)
                          .join(', ')})`}
                      <span
                        className="info-icon"
                        data-tooltip="Default is 10, and this will go up or down depending on whether host enables the RHP house rules for discarding or white privilege."
                      >
                        ℹ️
                      </span>
                    </span>
                  </div>
                )}

                {/* RHP DISCARD MECHANISM: Active discard mode */}
                {isDiscarding && (
                  <div className="discard-mode-active">
                    <div className="discard-instructions">
                      <strong>Discard Mode:</strong> Select 2 cards to trade for 1 new card
                      <br />
                      Selected: {cardsToDiscard.length}/2
                    </div>
                    <div className="discard-actions">
                      <button
                        className="discard-btn confirm-discard"
                        onClick={handleConfirmDiscard}
                        disabled={cardsToDiscard.length !== 2}
                      >
                        ✅ Confirm Discard
                      </button>
                      <button className="discard-btn cancel-discard" onClick={handleCancelDiscard}>
                        ❌ Cancel
                      </button>
                    </div>
                  </div>
                )}

                <button className="submit-btn" onClick={submitCards} disabled={!canSubmit}>
                  Submit Cards ({selectedCards.length}/{cardsNeeded})
                </button>
              </>
            )}
            {hasSubmitted && (
              <div className="submitted-message">
                <span>✅ Cards submitted! Waiting for other players...</span>
                <p>
                  Submissions: {submissionStatus.submitted}/{submissionStatus.total}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Preview area removed - selection now shown via in-place badges (#249, #250) */}

        <div className="hand-cards-container">
          {showFirstTimeTooltip && !selectedCards.length && (
            <div className="first-time-tooltip">
              <div className="tooltip-arrow"></div>
              <div className="tooltip-content">
                Click a card to select it. Click it again to deselect!
              </div>
            </div>
          )}

          <div className="hand-cards">
            {/* ISSUE #250: Single ordered list (regular + write-in cards) so selected
                cards can move to the front as a group; see orderedHandItems above. */}
            {orderedHandItems.map(({ card, isWriteIn, type }) => {
              // RHP DISCARD FIX: Check correct array based on mode (Issue #233)
              const isSelected = isDiscarding
                ? cardsToDiscard.some((selected) => selected.id === card.id)
                : selectedCards.some((selected) => selected.id === card.id);

              // RHP DISCARD FIX: Use correct handler based on mode (Issue #233)
              const getClickHandler = () => {
                if (isDiscarding) return () => handleDiscardCardToggle(card);
                if (gameState === 'playing' && !hasSubmitted) return () => handleCardSelect(card);
                return null;
              };

              // Calculate selection order for multi-card rounds (1-indexed)
              const selectionOrder =
                isSelected && cardsNeeded > 1
                  ? selectedCards.findIndex((s) => s.id === card.id) + 1
                  : null;

              // RHP Enhanced Wagering (#275): which pick-group this card belongs
              // to - the base submission (0) or one of the wagered chip sets
              // (1, 2, ...) - mirrors the same slicing SubmissionFactory uses
              // server-side to split a wagering submission into anonymous entries.
              const wagerGroup =
                selectionOrder !== null && isWagering
                  ? Math.floor((selectionOrder - 1) / baseCardsNeeded)
                  : null;

              return renderCard(
                card,
                type,
                isSelected,
                getClickHandler(),
                isWriteIn,
                selectionOrder,
                (el) => {
                  if (el) handCardRefs.current.set(card.id, el);
                  else handCardRefs.current.delete(card.id);
                },
                wagerGroup
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderVotingPhase = () => {
    return (
      <VotingPhase
        submissions={submissions}
        judge={judge}
        currentUsername={username}
        currentBlackCard={currentBlackCard}
        onCzarVote={handleCzarVote}
        rhpDoubleAwardEnabled={rhpDoubleAwardEnabled}
        canUseDoubleAward={
          rhpDoubleAwardEnabled &&
          !!judge?.id &&
          judge.id === currentPlayer?.id &&
          !doubleAwardUsedMap[judge.id]
        }
      />
    );
  };

  const renderResultsPhase = () => {
    return (
      <ResultsPhase
        round={round}
        roundWinner={roundWinner}
        roundWinners={roundWinners}
        isDoubleAwardRound={isDoubleAwardRound}
        winningSubmission={winningSubmission}
        pointsAwarded={pointsAwarded}
        pointsBreakdown={pointsBreakdown}
        previousBlackCard={previousBlackCard}
        scores={scores}
        judge={nextJudge || judge} // Use nextJudge from round-ended event, fallback to current
        roomCreatorPlayerId={roomCreatorPlayerId}
        resultsCountdown={resultsCountdown}
        maxRounds={maxRounds}
      />
    );
  };

  const renderGameFinished = () => {
    return (
      <GameFinished
        players={players}
        scores={scores}
        totalRoundsPlayed={totalRoundsPlayed}
        round={round}
        roomCreatorPlayerId={roomCreatorPlayerId}
        roomCode={roomCode}
        onLeaveGame={leaveGame}
      />
    );
  };

  // MEMORY LEAK FIX: Main useEffect with proper cleanup
  useEffect(() => {
    // Mark component as mounted
    console.log('🎮 GameRoom component mounting for room:', roomCode);
    window.gameRoomMountTime = Date.now();
    mountedRef.current = true;

    // Scroll to top when game room loads. On mobile, #root (not the window)
    // is the real scroll container - see the #root rules in index.css.
    window.scrollTo(0, 0);
    document.getElementById('root')?.scrollTo(0, 0);

    if (!requireAuth()) return;

    if (!roomCode) {
      console.error('Missing room data, redirecting to landing page');
      navigate('/');
      return;
    }

    // NEW: Check if user has seen tooltip before
    const hasSeenTooltip = localStorage.getItem('hasSeenCardSelectionTooltip');
    if (!hasSeenTooltip) {
      setShowFirstTimeTooltip(true);
    }

    initializeGameFromBackend();

    // MEMORY LEAK FIX: Cleanup function
    return () => {
      console.log('🧹 GameRoom component unmounting - cleaning up');
      mountedRef.current = false;

      // Track player leaving mid-game if game not finished
      if (!gameFinished && gameState !== 'finished') {
        trackPlayerDropoff(roomCode, 'mid_game');
      }

      // Event listeners are now cleaned up automatically by useGameWebSocket hook

      // TIMER SYSTEM: Clear timer interval
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Clear any timeouts stored in cleanup functions
      cleanupFunctionsRef.current.forEach((cleanup) => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
      cleanupFunctionsRef.current = [];

      // Clean up tooltip timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, username, navigate, requireAuth]);

  // Intentionally a plain function, not useCallback: it's redefined every
  // render so it always closes over current state, which is what the
  // eventHandlers useMemo below needs (its `connect` handler calls this
  // directly on every reconnect). The tradeoff is that useMemo recomputes
  // every render - already tolerated by useGameWebSocket's ref-based
  // listener registration, which doesn't re-subscribe on identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initializeGameFromBackend = async () => {
    if (isInitializingRef.current) {
      console.log('⏭️ initializeGameFromBackend already in progress, skipping duplicate call');
      return;
    }
    isInitializingRef.current = true;

    try {
      setConnectionStatus('connecting');
      trackConnectionStatus('connecting', roomCode);

      if (!socketClient.isSocketConnected()) {
        socketClient.connect();

        // MEMORY LEAK FIX: Promise with proper cleanup
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            socketClient.off('connect', tempConnectHandler);
            socketClient.off('connect_error', tempErrorHandler);
            reject(new Error('Connection timeout'));
          }, 10000);

          // Store cleanup function
          cleanupFunctionsRef.current.push(() => clearTimeout(timeout));

          if (socketClient.isSocketConnected()) {
            clearTimeout(timeout);
            resolve();
            return;
          }

          const tempConnectHandler = () => {
            clearTimeout(timeout);
            socketClient.off('connect', tempConnectHandler);
            socketClient.off('connect_error', tempErrorHandler);
            resolve();
          };

          const tempErrorHandler = (error) => {
            clearTimeout(timeout);
            socketClient.off('connect', tempConnectHandler);
            socketClient.off('connect_error', tempErrorHandler);
            reject(error);
          };

          socketClient.on('connect', tempConnectHandler);
          socketClient.on('connect_error', tempErrorHandler);
        });
      }

      // Check if component is still mounted
      if (!mountedRef.current) return;

      setConnectionStatus('connected');
      trackConnectionStatus('connected', roomCode);

      try {
        const backendGameState = await socketClient.getGameState(roomCode);

        // Check if component is still mounted before updating state
        if (!mountedRef.current) return;

        if (backendGameState && backendGameState.gameStarted) {
          console.log('Game state loaded:', backendGameState);

          setPlayers(backendGameState.players || []);
          setJudge(backendGameState.judge);
          setCurrentBlackCard(backendGameState.currentBlackCard);
          setRound((backendGameState.currentRound ?? 0) + 1);
          setScores(backendGameState.scores || {});

          // FIXED: Get maxRounds from backend game state
          if (backendGameState.maxRounds) {
            setMaxRounds(backendGameState.maxRounds);
            console.log('🎯 Max rounds set from backend:', backendGameState.maxRounds);
          }

          // WRITE-IN CARDS: Update writeInsEnabled from backend game state
          setWriteInsEnabled(backendGameState.writeInsEnabled || false);
          console.log('✍️ Write-ins enabled from backend:', backendGameState.writeInsEnabled);

          // RHP: Restore per-player max hand size from game state
          const player = backendGameState.players?.find((p) => p.username === username);
          if (player) {
            if (backendGameState.playerWhitePrivilegeBonuses?.[player.id] !== undefined)
              setPlayerWhitePrivilegeBonus(backendGameState.playerWhitePrivilegeBonuses[player.id]);
            if (backendGameState.playerDiscardCounts?.[player.id] !== undefined)
              setPlayerDiscardCount(backendGameState.playerDiscardCounts[player.id]);
          } else {
            // get-game-state only reads room state, it never (re)adds us - if we've
            // been fully removed (e.g. the disconnect grace period expired while
            // this tab was reloading), we'd otherwise sit here forever with an
            // undefined currentPlayer and an empty hand. Fall back to an actual
            // join so the server re-adds us and pushes an authoritative
            // 'game-started' that the already-registered event handlers pick up.
            console.log('🔄 Not found in players list on mount - attempting to rejoin room...');
            const { isSpectator: storedIsSpectator } = getRoomData();
            socketClient
              .joinRoom(roomCode, username, null, null, storedIsSpectator)
              .catch((joinError) => {
                console.warn('⚠️ Rejoin failed:', joinError.message);
                if (mountedRef.current) {
                  setError('Unable to rejoin the game. The room may no longer exist.');
                  setConnectionStatus('error');
                }
              });
          }
          setCurrentPlayer(player);

          // RHP: Restore Judge Double Award setting and usage map from game state
          if (backendGameState.rhpDoubleAwardEnabled !== undefined) {
            setRhpDoubleAwardEnabled(backendGameState.rhpDoubleAwardEnabled);
          }
          if (backendGameState.playerDoubleAwardUsed !== undefined) {
            setDoubleAwardUsedMap(backendGameState.playerDoubleAwardUsed);
          }

          // Check if current user is room creator or deputy on initial load
          if (backendGameState.roomCreatorPlayerId && player) {
            const isCreator =
              player.id === backendGameState.roomCreatorPlayerId ||
              player.playerId === backendGameState.roomCreatorPlayerId;
            setIsRoomCreator(isCreator);
            setRoomCreatorPlayerId(backendGameState.roomCreatorPlayerId); // Set the room creator player ID

            const isPlayerDeputy =
              player.id === backendGameState.deputyPlayerId ||
              player.playerId === backendGameState.deputyPlayerId;
            setIsDeputy(isPlayerDeputy);
            setDeputyPlayerId(backendGameState.deputyPlayerId);
            setCoHostEnabled(backendGameState.coHostEnabled || false);

            console.log('🔑 Initial room creator/deputy check:', {
              playerId: player.id,
              playerPlayerId: player.playerId,
              roomCreatorPlayerId: backendGameState.roomCreatorPlayerId,
              deputyPlayerId: backendGameState.deputyPlayerId,
              isRoomCreator: isCreator,
              isDeputy: isPlayerDeputy,
            });
          }

          setGameState(backendGameState.gamePhase || 'playing');
          console.log('🎯 gameState changed to:', backendGameState.gamePhase || 'playing');
        } else {
          console.log('Game not started, redirecting to lobby');
          navigate('/game-lobby');
          return;
        }
      } catch (stateError) {
        console.warn('Could not get game state:', stateError);

        // Room no longer exists (e.g. server restart) — redirect rather than spin forever
        if (stateError.message && stateError.message.toLowerCase().includes('room not found')) {
          console.log('🚪 Room no longer exists, redirecting to lobby...');
          if (mountedRef.current) navigate('/game-lobby');
          return;
        }

        // Otherwise request fresh state from backend via socket event
        // Socket will respond with game-started and game-state events containing authoritative data
        console.log('🔄 Requesting fresh game state from backend (no localStorage fallback)...');
        socketClient.emit('get-game-state', { roomCode });

        // Note: State will be populated by socket event handlers:
        // - handleGameStateUpdate (from game-state event)
        // - game-started event handlers in useGameEventHandlers
        // This ensures we always use fresh, authoritative data from the backend
      }
    } catch (error) {
      console.error('Failed to initialize game:', error);
      trackError('game_initialization_failed', {
        error: error.message,
        roomCode,
      });

      // Check if component is still mounted
      if (!mountedRef.current) return;

      setConnectionStatus('error');
      trackConnectionStatus('error', roomCode);
      setError(error.message);
    } finally {
      isInitializingRef.current = false;
    }
  };

  const handleKickPlayer = (targetPlayerId, playerName = 'this player') => {
    console.log('🔍 DEBUG handleKickPlayer called with:', targetPlayerId, typeof targetPlayerId);

    if (!roomCode || !socketClient.socket) {
      console.error('Cannot kick player: missing roomCode or socket');
      return;
    }

    // Safety check: ensure targetPlayerId is valid
    if (!targetPlayerId || typeof targetPlayerId !== 'string') {
      console.error('Cannot kick player: invalid player ID', targetPlayerId);
      alert('Error: Invalid player ID. Please refresh the page and try again.');
      return;
    }

    // Check if deputy is trying to kick room owner - show MUTINY confirmation
    const isKickingOwner = targetPlayerId === roomCreatorPlayerId;
    let confirmMessage;

    if (isKickingOwner) {
      confirmMessage = `🏴‍☠️ MUTINY!\n\nYou are the co-host trying to kick the game owner (${playerName}).\n\nAre you sure you want to remove the owner?`;
    } else {
      confirmMessage = `Are you sure you want to kick ${playerName}?`;
    }

    if (window.confirm(confirmMessage)) {
      console.log(`👢 Kicking player ${targetPlayerId} from room ${roomCode}`);
      const kickData = {
        roomCode: roomCode.toUpperCase(),
        targetPlayerId: targetPlayerId,
      };
      console.log('🔍 DEBUG: kickData object:', JSON.stringify(kickData));
      console.log('🔍 DEBUG: kickData.targetPlayerId:', kickData.targetPlayerId);
      socketClient.socket.emit('kick-player', kickData);
    }
  };

  const handleRemoveBot = (targetBotId, botName = 'this bot') => {
    console.log('🗑️ DEBUG handleRemoveBot called with:', targetBotId, typeof targetBotId);

    if (!roomCode || !socketClient.socket) {
      console.error('Cannot remove bot: missing roomCode or socket');
      return;
    }

    // Safety check: ensure targetBotId is valid
    if (!targetBotId || typeof targetBotId !== 'string') {
      console.error('Cannot remove bot: invalid bot ID', targetBotId);
      alert('Error: Invalid bot ID. Please refresh the page and try again.');
      return;
    }

    // Note: Confirmation dialog is already shown in Scoreboard.js
    console.log(`🗑️ Removing bot ${targetBotId} from room ${roomCode}`);
    const removeBotData = {
      roomCode: roomCode.toUpperCase(),
      targetBotId: targetBotId,
    };
    console.log('🔍 DEBUG: removeBotData object:', JSON.stringify(removeBotData));
    socketClient.socket.emit('remove-bot', removeBotData);
  };

  const handleDemoteToSpectator = (targetPlayerId, playerName = 'this player') => {
    console.log(
      '⬇️ DEBUG handleDemoteToSpectator called with:',
      targetPlayerId,
      typeof targetPlayerId
    );

    if (!roomCode || !socketClient.socket) {
      console.error('Cannot demote player: missing roomCode or socket');
      return;
    }

    // Safety check: ensure targetPlayerId is valid
    if (!targetPlayerId || typeof targetPlayerId !== 'string') {
      console.error('Cannot demote player: invalid player ID', targetPlayerId);
      alert('Error: Invalid player ID. Please refresh the page and try again.');
      return;
    }

    // Note: Confirmation dialog is already shown in Scoreboard.js
    console.log(`⬇️ Demoting player ${targetPlayerId} to spectator in room ${roomCode}`);
    const demoteData = {
      roomCode: roomCode.toUpperCase(),
      targetPlayerId: targetPlayerId,
    };
    console.log('🔍 DEBUG: demoteData object:', JSON.stringify(demoteData));
    socketClient.socket.emit('demote-to-spectator', demoteData);

    // Show notification
    addToast(`Moving ${playerName} to voyeur mode...`, 'info');
  };

  // SPECTATOR PROMOTION: Handle spectator self-promotion to player
  const handlePromoteSelf = () => {
    console.log('⬆️ Promoting self from spectator to player in room', roomCode);

    if (!roomCode || !socketClient.socket) {
      console.error('Cannot promote self: missing roomCode or socket');
      return;
    }

    socketClient.socket.emit('promote-spectator', {
      roomCode: roomCode.toUpperCase(),
    });

    // Show notification
    addToast('Joining game as active player...', 'info');
  };

  // CO-HOST SYSTEM: Designate deputy/co-host during active game
  const handleDesignateDeputy = (playerId) => {
    if (!roomCode || !socketClient.socket) {
      console.error('Cannot designate deputy: missing roomCode or socket');
      return;
    }

    if (!coHostEnabled) {
      alert('Co-host feature is not enabled for this room');
      return;
    }

    if (deputyPlayerId === playerId) {
      // Clear designation if clicking same player
      alert('To remove co-host, disable the co-host feature in the lobby');
      return;
    }

    // Designate player as deputy
    console.log(`🎖️ Designating player ${playerId} as deputy in room ${roomCode}`);
    socketClient.socket.emit('designate-deputy', {
      roomCode: roomCode.toUpperCase(),
      playerId,
    });
  };

  // SPECTATOR PROMOTION: Handle spectator requesting to join as player
  const handlePromoteSpectator = () => {
    console.log('🎟️ DEBUG handlePromoteSpectator called');

    if (!roomCode || !socketClient.socket) {
      console.error('Cannot promote spectator: missing roomCode or socket');
      return;
    }

    if (!allowSpectatorPromotion) {
      alert('Voyeur promotion is disabled in this room');
      return;
    }

    if (!roomHasSpace) {
      alert('Room is full. Wait for a slot to open up.');
      return;
    }

    console.log(`🎟️ Promoting spectator to player in room ${roomCode}`);
    const promoteData = {
      roomCode: roomCode.toUpperCase(),
    };
    socketClient.socket.emit('promote-spectator', promoteData);
  };

  // Track game phase changes
  useEffect(() => {
    if (gameState === 'playing' && currentBlackCard) {
      trackRoundStarted();
    }
  }, [gameState, currentBlackCard, trackRoundStarted]);

  // WAGERING SYSTEM FUNCTIONS
  const handleStartWager = () => {
    socketEmit('start-wager', {
      roomCode,
      chipsWagered: rhpEnhancedWageringEnabled ? chipsWagered : 1,
    });
  };

  const handleCancelWager = () => {
    socketEmit('cancel-wager', { roomCode });
  };

  // RHP: Enhanced Wagering — let the stepper stay adjustable after the wager
  // has started, instead of locking the amount in at the moment of clicking
  // "Bet". Syncs the new amount to the server and trims any already-selected
  // cards down to the new (smaller) target if the wager was reduced.
  const adjustWager = (newValue) => {
    const clamped = Math.min(Math.max(newValue, 1), maxChips);
    if (clamped === chipsWagered) return;
    setChipsWagered(clamped);
    if (isWagering) {
      socketEmit('update-wager', { roomCode, chipsWagered: clamped });
      const baseCardsNeeded = currentBlackCard?.pick || 1;
      const newCardsNeeded = baseCardsNeeded * (1 + clamped);
      setSelectedCards((prev) => prev.slice(0, newCardsNeeded));
    }
  };

  // RHP DISCARD MECHANISM: Handler functions
  const handleStartDiscard = () => {
    setIsDiscarding(true);
    setCardsToDiscard([]);
  };

  const handleDiscardCardToggle = (card) => {
    setCardsToDiscard((prev) => {
      const isAlreadySelected = prev.some((c) => c.id === card.id);
      if (isAlreadySelected) {
        return prev.filter((c) => c.id !== card.id);
      } else if (prev.length < 2) {
        return [...prev, card];
      }
      return prev;
    });
  };

  const handleConfirmDiscard = () => {
    if (cardsToDiscard.length !== 2) {
      addToast('Please select exactly 2 cards to discard', 'error');
      return;
    }

    console.log('🔍 Discard: Confirming discard of', cardsToDiscard.length, 'cards');
    const cardIds = cardsToDiscard.map((c) => c.id);

    socketClient.socket.once('discard-result', (response) => {
      console.log('🔍 Discard: Received response:', response);
      if (response?.success) {
        addToast('Traded 2 cards for 1 new card.', 'success');
        if (response.totalWhitePrivilegeBonus !== undefined)
          setPlayerWhitePrivilegeBonus(response.totalWhitePrivilegeBonus);
        if (response.totalDiscardCount !== undefined)
          setPlayerDiscardCount(response.totalDiscardCount);
        setIsDiscarding(false);
        setCardsToDiscard([]);
      } else {
        console.log('🔍 Discard: Failed -', response?.error);
        addToast(response?.error || 'Failed to discard cards', 'error');
      }
    });

    socketEmit('discard-cards', { roomCode, cardIds });
  };

  const handleCancelDiscard = () => {
    setIsDiscarding(false);
    setCardsToDiscard([]);
  };

  // CRITICAL FIX: Card selection with proper ID handling
  const handleCardSelect = (card) => {
    if (gameState !== 'playing') return;
    if (hasSubmitted) return;

    const usernameFromStorage = localStorage.getItem('username');
    const judgeUsername = judge?.username || 'Unknown';

    if (usernameFromStorage === judgeUsername) return;

    const baseCardsNeeded = currentBlackCard?.pick || 1;
    const cardsNeeded = baseCardsNeeded * (1 + (isWagering ? chipsWagered : 0));

    // CRITICAL: Use ID-based comparison for selection
    const isAlreadySelected = selectedCards.some((selected) => selected.id === card.id);

    if (isAlreadySelected) {
      // Remove from selection using ID comparison
      setSelectedCards(selectedCards.filter((selected) => selected.id !== card.id));
      console.log(
        `🔄 Deselected card: "${card.text}" (${card.id})${card.isWriteIn ? ' [WRITE-IN]' : ''}`
      );
    } else if (selectedCards.length < cardsNeeded) {
      // Add to selection preserving exact card object from backend or write-in
      const cardToAdd = {
        id: card.id,
        text: card.text,
        ...(card.isWriteIn && { isWriteIn: true }), // Include isWriteIn for custom cards
      };
      setSelectedCards([...selectedCards, cardToAdd]);
      console.log(
        `✅ Selected card: "${card.text}" (${card.id})${card.isWriteIn ? ' [WRITE-IN]' : ''}`
      );

      // NEW: Hide tooltip after first selection
      if (showFirstTimeTooltip) {
        localStorage.setItem('hasSeenCardSelectionTooltip', 'true');
        tooltipTimeoutRef.current = setTimeout(() => {
          setShowFirstTimeTooltip(false);
        }, 3000);
      }

      // Track if this is a multi-pick card
      if (cardsNeeded > 1) {
        trackFeature('multi_pick_card', {
          picks_required: cardsNeeded,
          roomCode,
        });
      }
    }

    console.log(
      `🎯 Selection status: ${selectedCards.length + (isAlreadySelected ? 0 : 1)}/${cardsNeeded}`
    );
  };

  // CRITICAL FIX: Card submission with preserved IDs + WAGERING
  const submitCards = () => {
    const baseCardsNeeded = currentBlackCard?.pick || 1;
    const expectedCards = baseCardsNeeded * (1 + (isWagering ? chipsWagered : 0));

    if (selectedCards.length !== expectedCards) return;
    if (hasSubmitted) return;

    console.log('🎯 Submitting cards (wagering:', isWagering, '):', selectedCards);

    // Track card submission
    trackCardSubmitted(roomCode);

    // CRITICAL: Send exactly what backend needs - preserve IDs and write-in flags
    const cardsToSubmit = selectedCards.map((card) => ({
      id: card.id,
      text: card.text,
      ...(card.isWriteIn && { isWriteIn: true }), // Include isWriteIn flag for write-in cards
    }));

    // Validate card text lengths before submitting
    const longCards = cardsToSubmit.filter((c) => c.text && c.text.length > 200);
    if (longCards.length > 0) {
      console.error('❌ Card text too long:', longCards);
      alert('Error: One or more cards have text that is too long. Please select different cards.');
      return;
    }

    console.log(
      '🚀 Final submission with IDs:',
      cardsToSubmit.map(
        (c) =>
          `"${c.text?.substring(0, 50)}${c.text?.length > 50 ? '...' : ''}" (${c.id})${c.isWriteIn ? ' [WRITE-IN]' : ''}`
      )
    );

    try {
      socketEmit('submit-cards', {
        roomCode: roomCode,
        cards: cardsToSubmit,
      });

      setHasSubmitted(true);
      console.log('✅ Cards submitted successfully');
    } catch (error) {
      console.error('❌ Error submitting cards:', error);
      alert('Error submitting cards. Please try again.');
      setHasSubmitted(false);
    }
  };

  const handleCzarVote = (submissionOrSubmissions) => {
    // RHP: Judge Double Award — an array of 2 submissions means a double-award vote
    const isDoubleAward = Array.isArray(submissionOrSubmissions);
    const submissions = isDoubleAward ? submissionOrSubmissions : [submissionOrSubmissions];

    console.log('Judge voting for:', submissions);

    // Track czar vote
    trackVoteCast(roomCode, true);

    const toWinningSubmission = (submission) => ({
      playerId: submission.playerId,
      playerName: submission.playerName,
      cards: submission.cards,
      id: submission.id,
    });

    socketEmit('czar-vote', {
      roomCode: roomCode,
      ...(isDoubleAward
        ? { winningSubmissions: submissions.map(toWinningSubmission) }
        : { winningSubmission: toWinningSubmission(submissions[0]) }),
    });

    console.log('✅ Vote submitted successfully');
  };

  // Temporary placeholders for socketEmit and hooks - will be properly initialized below
  let socketEmit = null;
  let setWriteInCards = () => {};
  let setHasSkippedThisRound = () => {};

  // GAME EVENT HANDLERS: Create event handlers hook with lazy initialization
  // This needs to be created before useGameWebSocket but after we know what setters we need
  const eventHandlersFromHook = useGameEventHandlers({
    mountedRef,
    setPlayers,
    setJudge,
    setCurrentBlackCard,
    setRound,
    setScores,
    setMaxRounds,
    setWriteInsEnabled,
    setGameState,
    username,
    gameState,
    setIsRoomCreator,
    setRoomCreatorPlayerId,
    setIsDeputy,
    setDeputyPlayerId,
    setCoHostEnabled,
    trackRoundStarted,
    trackConnectionStatus,
    setPlayerHand,
    setSubmissionStatus,
    setPresentationState,
    setSubmissions,
    setSelectedCards,
    setHasSubmitted,
    setWriteInCards: (value) => setWriteInCards(value),
    trackRoundCompleted,
    roomCode,
    round,
    setPreviousBlackCard,
    currentBlackCard,
    setNextJudge,
    setRoundWinner,
    setRoundWinners,
    setIsDoubleAwardRound,
    setWinningSubmission,
    setPointsAwarded,
    setPointsBreakdown,
    setResultsCountdown,
    cleanupFunctionsRef,
    setIsWagering,
    setWageringPlayers,
    setChipsWagered,
    socketClient,
    clearRoomData,
    navigate,
    roundSkippedShownRef,
    currentPlayer,
    setCurrentPlayer,
    players,
    scores,
    trackGameCompleted,
    totalRoundsPlayed,
    roomCreatorPlayerId,
    setGameFinished,
    setTotalRoundsPlayed,
    trackError,
    addToast,
    refetchWriteInStats,
    setError,
    setConnectionStatus,
    setTimerActive,
    setTimerType,
    setTimerDuration,
    setTimerRemaining,
    setTimerMessage,
    setTimerTargetPlayer,
    timerIntervalRef,
    setHasSkippedThisRound: (value) => setHasSkippedThisRound(value),
    setDesignatedFirstCzarId,
    setAllowSpectatorPromotion,
    setRhpDiscardEnabled,
    setRhpDoubleAwardEnabled,
    setDoubleAwardUsedMap,
    setRhpEnhancedWageringEnabled,
  });

  // WebSocket Event Handlers Object for useGameWebSocket hook
  const eventHandlers = useMemo(
    () => ({
      // game-state, game-state-update, and game-started all carry a full
      // getGameState() snapshot (initial load, mid-game broadcast, and
      // active-game reconnect respectively) - all three hydrate identically
      // via the shared handleGameStateUpdate pipeline (which owns the
      // designatedFirstCzarId/allowSpectatorPromotion/rhpDiscardEnabled/
      // rhpDoubleAwardEnabled/playerDoubleAwardUsed/rhpEnhancedWageringEnabled
      // extraction, plus currentPlayer refresh) instead of each listener
      // duplicating (or, in game-started's prior case, omitting) it.
      'game-state': eventHandlersFromHook.handleGameStateUpdate,
      'game-state-update': eventHandlersFromHook.handleGameStateUpdate,
      'game-started': eventHandlersFromHook.handleGameStateUpdate,
      'player-hand': eventHandlersFromHook.handlePlayerHand,
      'submission-update': eventHandlersFromHook.handleSubmissionUpdate,
      'presentation-phase-started': eventHandlersFromHook.handlePresentationPhaseStarted,
      'presentation-updated': eventHandlersFromHook.handlePresentationUpdated,
      'voting-phase-started': eventHandlersFromHook.handleVotingPhaseStarted,
      'round-ended': eventHandlersFromHook.handleRoundEnded,
      'round-started': eventHandlersFromHook.handleRoundStarted,
      'game-ended': eventHandlersFromHook.handleGameEnded,
      'scoring-discrepancy': eventHandlersFromHook.handleScoringDiscrepancy,
      error: eventHandlersFromHook.handleSocketError,
      'submission-error': eventHandlersFromHook.handleSubmissionError,
      connect: () => {
        eventHandlersFromHook.handleSocketConnect();
        // A reconnect - manual (the Reconnect button) or automatic - only
        // reopens the socket; it doesn't itself refetch game state. If the
        // very first attempt never succeeded (Finding 12/13's scenario),
        // gameState is still stuck at its initial 'loading' value with
        // nothing left to ever advance it. Retry the whole fetch now that
        // we're actually connected, instead of leaving the user stuck on
        // "Loading Game..." forever despite a healthy connection.
        if (gameState === 'loading') {
          initializeGameFromBackend();
        }
      },
      connect_error: eventHandlersFromHook.handleSocketConnectError,
      reconnect_failed: eventHandlersFromHook.handleSocketReconnectFailed,
      'wager-confirmed': eventHandlersFromHook.handleWagerConfirmed,
      'wager-cancelled': eventHandlersFromHook.handleWagerCancelled,
      'wager-update': eventHandlersFromHook.handleWagerUpdate,
      'white-privilege-awarded': (data) => {
        if (data.totalWhitePrivilegeBonus !== undefined)
          setPlayerWhitePrivilegeBonus(data.totalWhitePrivilegeBonus);
        if (data.totalDiscardCount !== undefined) setPlayerDiscardCount(data.totalDiscardCount);
        addToast(`📈 White Privilege: +1 card added to your hand limit!`, 'success', 5000);
      },
      'hand-update': (data) => {
        console.log('🔄 RHP: Hand updated after discard', data);
        if (data.hand) {
          setPlayerHand(data.hand);
        }
      },
      'timer-started': eventHandlersFromHook.handleTimerStarted,
      'timer-expired': eventHandlersFromHook.handleTimerExpired,
      'timer-cancelled': eventHandlersFromHook.handleTimerCancelled,
      'black-card-changed': eventHandlersFromHook.handleBlackCardChanged,
      'black-card-skipped': eventHandlersFromHook.handleBlackCardSkipped,
      'czar-confirmed-black-card': eventHandlersFromHook.handleCzarConfirmedBlackCard,
      'player-kicked': eventHandlersFromHook.handlePlayerKicked,
      'player-demoted': eventHandlersFromHook.handlePlayerDemoted,
      'round-skipped': eventHandlersFromHook.handleRoundSkipped,
      'bot-removed-for-human': (data) => {
        console.log('🤖➡️👤 Bot removed for human:', data);
        addToast(data.message, 'info');
      },
      'deck-reset': eventHandlersFromHook.handleDeckReset,
      'first-czar-designated': (data) => {
        console.log('💩 First Judge designated:', data);
        setDesignatedFirstCzarId(data.playerId);
      },
      'first-czar-designation-cleared': () => {
        console.log('💩 First Judge designation cleared');
        setDesignatedFirstCzarId(null);
      },
      // SPECTATOR PROMOTION: Handle room capacity updates
      'room-capacity-update': (data) => {
        console.log('🎟️ Room capacity update:', data);
        setRoomHasSpace(data.hasSpace);
      },
      // SPECTATOR PROMOTION: Handle spectator promoted event
      'spectator-promoted': (data) => {
        console.log('🎟️ Spectator promoted:', data);
        // Room state will be updated via room-updated event
      },
      // SPECTATOR PROMOTION: Handle promotion success
      'promotion-success': (data) => {
        console.log('🎟️ Promotion success:', data.message);
        alert(data.message);
      },
    }),
    [eventHandlersFromHook, setRoomHasSpace, addToast, gameState, initializeGameFromBackend]
  );

  // Use the WebSocket hook for event management
  const socketHookResult = useGameWebSocket(roomCode, eventHandlers, [username]);
  socketEmit = socketHookResult.emit;

  // BLACK CARD WRITE-IN: Initialize black card write-in hook
  const blackCardWriteInHook = useBlackCardWriteIn(
    socketEmit,
    roomCode,
    gameState,
    round,
    trackFeature
  );
  const showBlackCardModal = blackCardWriteInHook.showBlackCardModal;
  const blackCardText = blackCardWriteInHook.blackCardText;
  const setBlackCardText = blackCardWriteInHook.setBlackCardText;
  const czarWriteInTimer = blackCardWriteInHook.czarWriteInTimer;
  const hasSkippedThisRound = blackCardWriteInHook.hasSkippedThisRound;
  setHasSkippedThisRound = blackCardWriteInHook.setHasSkippedThisRound;
  const openBlackCardModal = blackCardWriteInHook.openBlackCardModal;
  const closeBlackCardModal = blackCardWriteInHook.closeBlackCardModal;
  const createCustomBlackCard = blackCardWriteInHook.createCustomBlackCard;
  const skipBlackCard = blackCardWriteInHook.skipBlackCard;
  const confirmBlackCard = blackCardWriteInHook.confirmBlackCard;

  // WRITE-IN CARDS: Initialize write-in cards hook
  const writeInCardsHook = useWriteInCards(
    socketEmit,
    currentPlayer,
    currentBlackCard,
    addToast,
    refetchWriteInStats,
    trackFeature,
    writeInStats,
    writeInsEnabled
  );
  const writeInCards = writeInCardsHook.writeInCards;
  setWriteInCards = writeInCardsHook.setWriteInCards;
  const writeInText = writeInCardsHook.writeInText;
  const setWriteInText = writeInCardsHook.setWriteInText;
  const showWriteInModal = writeInCardsHook.showWriteInModal;
  const openWriteInModal = writeInCardsHook.openWriteInModal;
  const closeWriteInModal = writeInCardsHook.closeWriteInModal;
  const createWriteInCardFromHook = writeInCardsHook.createWriteInCard;
  const removeWriteInCard = writeInCardsHook.removeWriteInCard;

  // ISSUE #250: Reorder hand so selected cards move to the front (top-left),
  // grouping them together while submitting. Frozen during discard mode, since
  // cardsToDiscard is a separate selection concept from selectedCards.
  // Intentionally NOT frozen on hasSubmitted - selectedCards is locked at that
  // point anyway, and freezing it snapped the just-grouped cards back to their
  // natural position the instant you hit submit.
  const orderedHandItems = useMemo(() => {
    const allItems = [
      ...playerHand.map((card) => ({ card, isWriteIn: false, type: 'white' })),
      ...writeInCards.map((card) => ({ card, isWriteIn: true, type: 'write-in' })),
    ];

    if (isDiscarding) {
      return allItems;
    }

    const selectedIds = new Set(selectedCards.map((c) => c.id));
    const selectedItems = selectedCards
      .map((selected) => allItems.find((item) => item.card.id === selected.id))
      .filter(Boolean);
    const unselectedItems = allItems.filter((item) => !selectedIds.has(item.card.id));

    return [...selectedItems, ...unselectedItems];
  }, [playerHand, writeInCards, selectedCards, isDiscarding]);

  // ISSUE #250: FLIP animation so the reorder above animates into place
  // instead of snapping (CSS transitions can't animate a plain DOM reorder).
  const handCardRefs = useRef(new Map());
  const prevHandCardRects = useRef(new Map());

  useLayoutEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const newRects = new Map();
    handCardRefs.current.forEach((el, id) => {
      newRects.set(id, el.getBoundingClientRect());
    });

    if (!prefersReducedMotion) {
      newRects.forEach((newRect, id) => {
        const el = handCardRefs.current.get(id);
        const oldRect = prevHandCardRects.current.get(id);
        if (!el || !oldRect) return;

        const deltaX = oldRect.left - newRect.left;
        const deltaY = oldRect.top - newRect.top;
        if (!deltaX && !deltaY) return;

        el.style.transition = 'none';
        el.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        el.getBoundingClientRect(); // force reflow so the transform above applies before transitioning
        el.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        el.style.transform = '';
        el.addEventListener(
          'transitionend',
          () => {
            el.style.transition = '';
          },
          { once: true }
        );
      });
    }

    prevHandCardRects.current = newRects;
  }, [orderedHandItems]);

  // Wrapper to add selected cards to state after creating write-in
  const createWriteInCard = useCallback(() => {
    const newCard = createWriteInCardFromHook();
    if (newCard) {
      setSelectedCards((prev) => [...prev, newCard]);
    }
  }, [createWriteInCardFromHook]);

  // Presentation handlers with socketEmit
  const handleStartPresentation = useCallback(() => {
    console.log('🎭 Starting presentation phase');
    socketEmit('start-presentation', { roomCode });
  }, [socketEmit, roomCode]);

  const handleNextSubmission = useCallback(() => {
    console.log('🎭 Moving to next submission');
    socketEmit('next-submission', { roomCode });
  }, [socketEmit, roomCode]);

  // PENDING SELECTIONS: Broadcast card selection changes to server
  useEffect(() => {
    // Don't emit card selection events for spectators
    if (currentPlayer?.isSpectator) {
      return;
    }

    if (socketClient.isSocketConnected() && socketEmit) {
      const selectedCardIds =
        gameState === 'playing' && !hasSubmitted ? selectedCards.map((card) => card.id) : []; // Clear selections if not in playing state or already submitted

      socketEmit('update-card-selection', {
        roomCode,
        selectedCardIds,
      });
      console.log(`📝 Broadcasted selection update: ${selectedCardIds.length} cards`);
    }
  }, [selectedCards, gameState, hasSubmitted, roomCode, socketEmit, currentPlayer?.isSpectator]);

  const handleAddBot = async () => {
    if (!roomCode || !socketClient.socket) {
      console.error('Cannot add bot: missing roomCode or socket');
      return;
    }

    try {
      console.log(`🤖 Adding bot to room ${roomCode}`);
      await socketEmit('add-bot', { roomCode: roomCode.toUpperCase() });
      trackFeature('add_bot_mid_game');
    } catch (error) {
      console.error('Failed to add bot:', error);
      const errorMessage = error?.message || error || 'Failed to add bot';
      addToast(errorMessage, 'error', 4000);
    }
  };

  const leaveGame = () => {
    // Track if player is leaving mid-game
    if (gameState !== 'finished' && gameFinished !== true) {
      trackPlayerDropoff(roomCode, gameState === 'playing' ? 'mid_game' : 'lobby');
    }

    // Event listeners are now cleaned up automatically by useGameWebSocket hook

    socketClient.leaveRoom(roomCode);
    socketClient.disconnect();
    clearRoomData();
    navigate('/');
  };

  // A connection that never succeeded even once (e.g. the mount-time fetch
  // in initializeGameFromBackend failed) leaves gameState stuck at its
  // initial 'loading' value forever - nothing else ever advances it. Without
  // excluding the terminal failure statuses here, that permanently traps the
  // render on this screen even once connectionStatus correctly reaches
  // 'error' or 'reconnect_failed', hiding the screens below that exist
  // specifically to handle those cases.
  if (
    (gameState === 'loading' || connectionStatus === 'connecting') &&
    connectionStatus !== 'error' &&
    connectionStatus !== 'reconnect_failed'
  ) {
    return (
      <div className="game-room">
        <div className="loading">
          <h2>Loading Game...</h2>
          <p>Room Code: {roomCode}</p>
          <p>Status: {connectionStatus}</p>
        </div>
      </div>
    );
  }

  if (connectionStatus === 'reconnect_failed') {
    return (
      <div className="game-room">
        <div className="error">
          <h2>Connection Lost</h2>
          <p>We couldn't reconnect you automatically. Your seat is still held in the game.</p>
          <button
            onClick={() => {
              setConnectionStatus('connecting');
              socketClient.manualReconnect();
            }}
          >
            Reconnect
          </button>
          <button onClick={() => navigate('/game-lobby')}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  if (connectionStatus === 'error' || error) {
    return (
      <div className="game-room">
        <div className="error">
          <h2>Connection Error</h2>
          <p>{typeof error === 'string' ? error : error?.message || 'Connection error'}</p>
          <button onClick={() => navigate('/game-lobby')}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  // ENHANCED: Show professional game finished screen
  if (gameState === 'finished' || gameFinished) {
    return <div className="game-room">{renderGameFinished()}</div>;
  }

  // NEW: Determine which black card to show based on game state
  const displayedBlackCard =
    gameState === 'results' && previousBlackCard ? previousBlackCard : currentBlackCard;

  // DEBUGGING: Log render state
  console.log('🎨 Current render - gameState:', gameState, 'submissions:', submissions.length);

  return (
    <div className="game-room">
      <div className="game-header">
        <div className="room-info">
          <h1>Carnivores Adapting Terribly</h1>
          <p>
            Room: {roomCode} | Round: {round}/{maxRounds}
          </p>
          <div className="user-display">
            <span className="playing-as">
              {currentPlayer?.isSpectator ? '👁️ Voyeuring as:' : 'Playing as:'}{' '}
              <strong>{username}</strong>
            </span>
          </div>
        </div>
        <button onClick={leaveGame} className="leave-btn">
          Leave Game
        </button>
      </div>

      {/* TIMER SYSTEM: Display countdown timer when active */}
      {timerActive && (
        <div
          className={`timer-display ${timerType === 'last_player' ? 'last-player-timer' : 'escalation-timer'}`}
        >
          <div className="timer-content">
            <div className="timer-icon">⏰</div>
            <div className="timer-info">
              <div className="timer-message">{timerMessage}</div>
              <div className="timer-countdown">
                <span className="timer-remaining">{timerRemaining}</span>
                <span className="timer-unit">seconds remaining</span>
              </div>
            </div>
            <div className="timer-progress">
              <div
                className="timer-progress-bar"
                style={{
                  width: `${(timerRemaining / timerDuration) * 100}%`,
                  backgroundColor:
                    timerRemaining <= 10 ? '#ff4444' : timerRemaining <= 30 ? '#ff8800' : '#4CAF50',
                }}
              ></div>
            </div>
          </div>
        </div>
      )}

      <div className="game-content">
        <div className="main-game-area">
          <div className="black-card-area">
            <h2>Current Card</h2>
            {displayedBlackCard &&
              (gameState === 'playing' ||
                gameState === 'presentation' ||
                gameState === 'voting' ||
                gameState === 'results' ||
                (gameState === 'czar-picking' && username === (judge?.username || ''))) && (
                <BlackCard card={displayedBlackCard} />
              )}
            {gameState === 'czar-picking' && username !== (judge?.username || '') && (
              <div className="black-card-hidden">
                <p>⏳ Waiting for {judge?.username || 'Judge'} to choose...</p>
              </div>
            )}
          </div>

          <div className="game-status">
            {gameState === 'czar-picking' && (
              <p>{judge?.username || 'Judge'} is choosing the question card...</p>
            )}
            {gameState === 'playing' && <p>Players are selecting cards...</p>}
            {gameState === 'presentation' && <p>Presenting submissions...</p>}
            {gameState === 'voting' && (
              <p>{judge?.username || 'Judge'} is choosing the winner...</p>
            )}
            {gameState === 'results' && <p>Round complete!</p>}
          </div>

          {console.log(
            '🎯 Checking gameState for conditional render:',
            gameState,
            'voting?',
            gameState === 'voting'
          ) || ''}
          {gameState === 'presentation' && renderPresentationPhase()}
          {gameState === 'voting' && renderVotingPhase()}
          {gameState === 'results' && renderResultsPhase()}
        </div>

        <div className="sidebar">
          <Scoreboard
            players={players}
            scores={scores}
            isRoomCreator={isRoomCreator}
            isDeputy={isDeputy}
            username={username}
            wageringPlayers={wageringPlayers}
            judge={judge}
            roomCreatorPlayerId={roomCreatorPlayerId}
            deputyPlayerId={deputyPlayerId}
            coHostEnabled={coHostEnabled}
            designatedFirstCzarId={designatedFirstCzarId}
            currentRound={round}
            onKickPlayer={handleKickPlayer}
            onRemoveBot={handleRemoveBot}
            onDemoteToSpectator={handleDemoteToSpectator}
            onDesignateDeputy={handleDesignateDeputy}
            onPromoteSelf={handlePromoteSelf}
            rhpDoubleAwardEnabled={rhpDoubleAwardEnabled}
            doubleAwardUsedMap={doubleAwardUsedMap}
          />

          {/* Add Bot button - visible to room creator and deputy during active game */}
          {(isRoomCreator || isDeputy) && (
            <div className="sidebar-controls">
              <button
                onClick={handleAddBot}
                className="add-bot-button"
                disabled={
                  players.filter((p) => p.isBot).length >= 4 ||
                  players.filter((p) => !p.isSpectator).length >= 6
                }
              >
                🤖 Add Bot ({players.filter((p) => p.isBot).length}/4)
              </button>
            </div>
          )}
        </div>
      </div>

      {(gameState === 'playing' || gameState === 'czar-picking') && renderPlayerHand()}

      {/* WRITE-IN CARDS: Modal for creating custom cards */}
      <WriteInModal
        show={showWriteInModal}
        onClose={closeWriteInModal}
        writeInText={writeInText}
        setWriteInText={setWriteInText}
        onCreateCard={createWriteInCard}
        writeInStats={writeInStats}
        writeInLimits={writeInLimits}
        blackCardPickCount={currentBlackCard?.pick || 1}
      />

      {/* BLACK CARD WRITE-IN: Modal for Judge to create custom black cards */}
      <BlackCardModal
        show={showBlackCardModal}
        onClose={closeBlackCardModal}
        blackCardText={blackCardText}
        setBlackCardText={setBlackCardText}
        onCreateCard={createCustomBlackCard}
        czarWriteInTimer={czarWriteInTimer}
      />

      {/* TOAST NOTIFICATIONS: Display rate limit and error messages */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default GameRoom;
